import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import Group from '../../../components/form/elements/group';
import Readonly from '../../../components/form/elements/readonly';
import Text from '../../../components/form/elements/text';
import Form, { SubmitType } from '../../../components/form';
import { useHistory, useParams } from 'react-router-dom';
import { useService } from '../../../services/services';
import Checkbox from '../../../components/form/elements/checkbox';
import Button from '../../../components/form/elements/button';
import Link from '../../../components/form/elements/link';
import Role from '../../../interfaces/role';
import { useObservableState } from 'observable-hooks';
import User from '../../../interfaces/user';
import { map } from 'rxjs/operators';
import { route } from '../../../services/utils';
import { INTERNAL_USERS_VIEW } from '../../../router/routes';
import { useTranslation } from 'react-i18next';

const editUserSchema = yup.object().shape({
  username: yup.string().max(255).required(),
  name: yup.string().max(255),
  email: yup.string().max(255).email(),
  blocked: yup.boolean(),
  roles: yup.object(),
});

export default function EditUser() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const { userService, roleService } = useService();
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);

  const [roles] = useObservableState<Role[]>(() => roleService.all(), null);
  const [user] = useObservableState<User>(
    () =>
      userService.show(id).pipe(
        map((user: User) => {
          const mappedUser = { ...user, roles: {} as any };

          user.roles.forEach((role: string) => {
            mappedUser.roles[role] = true;
          });

          return mappedUser;
        })
      ),
    null
  );

  useEffect(() => {
    if (user !== null && roles !== null) {
      const roleElements = roles.map((role: Role) => {
        return Checkbox(`roles.${role.name}`, role.name);
      });

      setBodyElements([
        Group([
          Readonly([Text('username'), Text('name'), Text('email')]),
          Checkbox('blocked'),
          ...roleElements,
        ]),
      ]);

      setFooterElements([
        Button('save'),
        Link(route(INTERNAL_USERS_VIEW, { id }), 'cancel'),
      ]);
    }
  }, [id, user, roles, setBodyElements, setFooterElements]);

  const submit = useCallback<SubmitType<User>>(
    (form) => [
      userService.update(id, form),
      (response: User) =>
        history.push(route(INTERNAL_USERS_VIEW, { id: response.id })),
    ],
    [id, userService, history]
  );

  return (
    <Form
      title={user ? user.username : t('pages.internal.users.Edit user')}
      mode={'edit'}
      body={bodyElements}
      footer={footerElements}
      values={user}
      submit={submit}
      schema={editUserSchema}
    />
  );
}
